import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/Layout';
import { FilterTabs, Modal } from '../../components/Common';
import { OrderStatus } from '../../types/orders.types';
import {
  reportedProblemsService,
  type EnrichedReportedProblem,
  type ReportedProblemStatus,
} from '../../services';
import ProblemDetailsModal from './ProblemDetailsModal';

type StatusFilter = 'all' | ReportedProblemStatus;

const FILTER_TABS: { label: string; value: StatusFilter }[] = [
  { label: 'Open', value: 'open' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'All', value: 'all' },
];

const PAGE_SIZE = 50;

const formatDateTime = (iso?: string): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const getErrorMessage = (err: unknown, fallback: string): string => {
  const e = err as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    fallback
  );
};

const getOrderStatusColor = (
  status: OrderStatus,
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case OrderStatus.APPROVED:
    case OrderStatus.COMPLETED:
      return 'success';
    case OrderStatus.CANCELLED:
    case OrderStatus.DECLINED:
      return 'error';
    case OrderStatus.REFUNDED:
    case OrderStatus.CHANGING:
      return 'warning';
    case OrderStatus.IN_PROGRESS:
    case OrderStatus.DELIVERY:
      return 'info';
    default:
      return 'default';
  }
};

/** Primary/secondary stacked cell, matching the Orders page row layout. */
const StackedCell = ({ primary, secondary }: { primary: string; secondary?: string }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <Typography
      sx={{
        fontSize: 'var(--palette-light-font-size-default-value)',
        color: 'var(--palette-light-text-primary-value-hex)',
      }}
    >
      {primary}
    </Typography>
    {secondary && (
      <Typography
        sx={{
          fontSize: 'var(--palette-light-font-size-small-value)',
          color: 'var(--palette-light-text-secondary-value-hex)',
        }}
      >
        {secondary}
      </Typography>
    )}
  </Box>
);

const ReportedProblems: React.FC = () => {
  const [items, setItems] = useState<EnrichedReportedProblem[]>([]);
  const [cursor, setCursor] = useState<Record<string, unknown> | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const [detailsTarget, setDetailsTarget] = useState<EnrichedReportedProblem | null>(null);
  const [resolveTarget, setResolveTarget] = useState<EnrichedReportedProblem | null>(null);
  const [resolving, setResolving] = useState(false);

  // Fetch first page whenever the filter changes or a refresh is requested.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await reportedProblemsService.list({
          ...(statusFilter !== 'all' && { status: statusFilter }),
          limit: PAGE_SIZE,
        });
        if (cancelled) return;
        setItems(res.items);
        setCursor(res.lastEvaluatedKey);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(getErrorMessage(err, 'Failed to load reported problems'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [statusFilter, refreshCounter]);

  const loadMore = useCallback(async () => {
    if (!cursor) return;
    setLoading(true);
    setError(null);
    try {
      const res = await reportedProblemsService.list({
        ...(statusFilter !== 'all' && { status: statusFilter }),
        limit: PAGE_SIZE,
        lastEvaluatedKey: cursor,
      });
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.lastEvaluatedKey);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load more reported problems'));
    } finally {
      setLoading(false);
    }
  }, [cursor, statusFilter]);

  const handleResolve = useCallback(async () => {
    if (!resolveTarget) return;
    setResolving(true);
    try {
      const updated = await reportedProblemsService.resolve(resolveTarget.problemId);
      setItems((prev) =>
        prev.map((item) => (item.problemId === updated.problemId ? updated : item))
      );
      setDetailsTarget((prev) =>
        prev && prev.problemId === updated.problemId ? updated : prev
      );
      setResolveTarget(null);
      toast.success('Problem marked as resolved');
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, 'Failed to resolve problem'));
    } finally {
      setResolving(false);
    }
  }, [resolveTarget]);

  const startResolve = useCallback((problem: EnrichedReportedProblem) => {
    setDetailsTarget(null);
    setResolveTarget(problem);
  }, []);

  return (
    <Box
      className="page-container"
      sx={{
        padding: { xs: '24px 16px', sm: '40px 40px', md: '40px 80px' },
        gap: '24px',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <PageHeader title="Reported Problems" />
      <Typography variant="body2" color="text.secondary">
        Problems reported by users from the mobile app, with the reporting user and any linked
        order.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FilterTabs
          tabs={FILTER_TABS}
          activeTab={statusFilter}
          onTabChange={(value) => setStatusFilter(value as StatusFilter)}
        />
        <Button
          variant="outlined"
          onClick={() => setRefreshCounter((c) => c + 1)}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Paper sx={{ overflow: 'auto', flex: 1 }}>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Problem</TableCell>
                <TableCell>Reported by</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reported</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No reported problems
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((problem) => (
                  <TableRow key={problem.problemId} hover>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={problem.description}
                      >
                        {problem.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={problem.user.imageProfile || undefined}
                          alt={problem.user.name}
                          sx={{ width: 28, height: 28, fontSize: 12 }}
                        />
                        <StackedCell
                          primary={problem.user.name}
                          secondary={problem.user.phone || undefined}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {problem.order ? (
                        <StackedCell
                          primary={problem.order.orderNumber}
                          secondary={problem.order.productName}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: 'var(--palette-light-text-tertiary-value-hex)' }}
                        >
                          Not linked
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {problem.vendor ? (
                        <StackedCell
                          primary={problem.vendor.companyName}
                          secondary={problem.vendor.status}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: 'var(--palette-light-text-tertiary-value-hex)' }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={problem.status === 'resolved' ? 'Resolved' : 'Open'}
                          size="small"
                          color={problem.status === 'resolved' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                        {problem.order && (
                          <Chip
                            label={problem.order.status}
                            size="small"
                            variant="outlined"
                            color={getOrderStatusColor(problem.order.status)}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDateTime(problem.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setDetailsTarget(problem)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={problem.status === 'resolved'}
                          onClick={() => setResolveTarget(problem)}
                        >
                          Resolve
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {cursor && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" onClick={loadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}

      <ProblemDetailsModal
        problem={detailsTarget}
        onClose={() => setDetailsTarget(null)}
        onResolve={startResolve}
      />

      {/* Resolve confirmation modal */}
      <Modal
        open={resolveTarget !== null}
        onClose={() => (resolving ? undefined : setResolveTarget(null))}
        title="Resolve Problem"
      >
        {resolveTarget && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2">
              Mark this reported problem as resolved? This action cannot be undone.
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {resolveTarget.description}
            </Typography>
            {resolveTarget.order && (
              <Typography variant="body2" color="text.secondary">
                Order {resolveTarget.order.orderNumber} · {resolveTarget.user.name}
                {resolveTarget.user.phone ? ` · ${resolveTarget.user.phone}` : ''}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setResolveTarget(null)}
                disabled={resolving}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={handleResolve} disabled={resolving}>
                {resolving ? 'Resolving…' : 'Resolve'}
              </Button>
            </Box>
          </Box>
        )}
      </Modal>
    </Box>
  );
};

export default ReportedProblems;
